import { Box, Center, Checkbox, HStack, Input, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useInViewport } from "react-in-viewport"
import { IBeaconsByFilterQueryVariables, useIBeaconsByFilterQuery } from "../../graphql"

export type IBeaconSelectorProps = {
	onUpdate: (ibeaconId?: string) => void
	value?: string
}

export const IBeaconSelector: React.FC<IBeaconSelectorProps> = ({ value, onUpdate }) => {
	const [variables, setVariables] = useState<IBeaconsByFilterQueryVariables>({
		filter: { keyword: "", uuid: undefined, major: undefined, minor: undefined },
		pagination: { limit: 20, page: 1 },
	})

	const [{ data, fetching, error }] = useIBeaconsByFilterQuery({ variables })

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.ibeaconsByFilter.hasNextPage) {
			setVariables((prev) => ({
				...prev,
				pagination: {
					...prev.pagination,
					page: (prev.pagination.page || 0) + 1,
				},
			}))
		}
	}, [data, fetching])

	const ref = useRef()

	const { inViewport } = useInViewport(ref as any, { threshold: 0.25 })

	useEffect(() => {
		if (inViewport) {
			nextPage()
		}
	}, [inViewport, nextPage])

	const [keyword, setKeyword] = useState("")
	const [uuid, setUuid] = useState("")
	const [major, setMajor] = useState("")
	const [minor, setMinor] = useState("")

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setVariables((prev) => ({
				...prev,
				filter: {
					...prev.filter,
					keyword,
					uuid,
					major: major ? Number(major) : undefined,
					minor: minor ? Number(minor) : undefined,
				},
				pagination: { ...prev.pagination, page: 1 },
			}))
		}, 400)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword, uuid, major, minor])

	const handleChange = (ibeaconId: string, isChecked: boolean) => {
		if (isChecked) {
			onUpdate(ibeaconId)
		} else {
			onUpdate(undefined)
		}
	}

	const isIBeaconSelected = useCallback((ibeaconId: string) => value === ibeaconId, [value])

	const isSelectionActive = useMemo(() => Boolean(value), [value])

	const availableIBeacons = useMemo(() => data?.ibeaconsByFilter.ibeacons.filter((ibeacon) => !ibeacon.assignedToZoneId) || [], [data])

	return (
		<VStack w="full" align="stretch">
			<HStack w="full" flexWrap="wrap" justify="space-between">
				<Input flex="1" variant="filled" bgColor="grayscale.input-background" placeholder="Search" _placeholder={{ color: "grayscale.placeholer" }} value={keyword} onChange={(e) => setKeyword(e.target.value)} />
				<Input flex="1" variant="filled" bgColor="grayscale.input-background" placeholder="UUID" _placeholder={{ color: "grayscale.placeholer" }} value={uuid} onChange={(e) => setUuid(e.target.value)} />
				<Input
					flex="1"
					type="number"
					variant="filled"
					bgColor="grayscale.input-background"
					placeholder="Major"
					_placeholder={{ color: "grayscale.placeholer" }}
					value={major}
					onChange={(e) => setMajor(e.target.value)}
				/>
				<Input
					flex="1"
					type="number"
					variant="filled"
					bgColor="grayscale.input-background"
					placeholder="Minor"
					_placeholder={{ color: "grayscale.placeholer" }}
					value={minor}
					onChange={(e) => setMinor(e.target.value)}
				/>
			</HStack>
			{fetching && !availableIBeacons.length ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading iBeacons</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : !availableIBeacons.length ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						No unassigned iBeacons found.
					</Text>
				</Center>
			) : (
				<VStack w="full" align="stretch" h="full" maxH={{ base: "sm", xl: "lg" }} overflowY="auto" spacing={0}>
					<Table>
						<Thead pos="sticky" top="0" w="full" zIndex={10} bgColor="white">
							<Tr>
								<Th>
									<Checkbox
										isChecked={isSelectionActive}
										onChange={() => {
											onUpdate(undefined)
										}}
									>
										<Text fontSize="xs" textTransform="lowercase">
											{value ? 1 : 0} selected
										</Text>
									</Checkbox>
								</Th>
								<Th>iBeacon</Th>
								<Th>UUID</Th>
								<Th>Major/Minor</Th>
							</Tr>
						</Thead>
						<Tbody>
							{availableIBeacons.map((ibeacon) => (
								<Tr key={ibeacon._id}>
									<Td>
										<Checkbox isChecked={isIBeaconSelected(ibeacon._id)} onChange={(e) => handleChange(ibeacon._id, e.target.checked)} />
									</Td>
									<Td>{ibeacon.label.name}</Td>
									<Td>{ibeacon.uuid}</Td>
									<Td>
										{ibeacon.major} / {ibeacon.minor}
									</Td>
								</Tr>
							))}
							<Box w="full" h="2" ref={ref as any} />
						</Tbody>
					</Table>
					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more iBeacons</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			)}
		</VStack>
	)
}
